import { useEffect, useState } from 'react'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { QRCodeSVG } from 'qrcode.react'
import { checkinService, type MyCheckin } from '../services/checkinService'
import PageContainer from '../components/ui/PageContainer'
import { EmptyState, LoadingState } from '../components/ui/States'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'

/**
 * The attendee's personal check-in badge — a QR code staff scan on arrival.
 */
export default function BadgePage() {
  const { isAuthenticated } = useAttendeeAuth()
  const [data, setData] = useState<MyCheckin | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    checkinService
      .me()
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  if (loading) return <LoadingState />
  if (!isAuthenticated)
    return (
      <PageContainer>
        <EmptyState
          title="Sign in to view your badge"
          description="Your check-in QR code is available once you're signed in."
        />
      </PageContainer>
    )
  if (error || !data)
    return (
      <PageContainer>
        <EmptyState title="Badge unavailable" description="Please try again shortly." />
      </PageContainer>
    )

  return (
    <PageContainer title="My badge" subtitle="Show this at check-in">
      <Stack alignItems="center" spacing={3} sx={{ mt: 2 }}>
        {data.checkedIn && (
          <Chip
            icon={<CheckCircleRoundedIcon />}
            color="success"
            label="Checked in"
            sx={{ fontWeight: 700 }}
          />
        )}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: 2, display: 'inline-flex' }}>
            <QRCodeSVG value={data.token} size={220} level="M" />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
            {data.fullName || data.email}
          </Typography>
          {data.fullName && (
            <Typography variant="body2" color="text.secondary">
              {data.email}
            </Typography>
          )}
        </Paper>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 320 }}>
          Have this code ready when you arrive — a team member will scan it to
          check you in.
        </Typography>
      </Stack>
    </PageContainer>
  )
}
